import { DateTime } from 'luxon';

/**
 * Presentation helpers shared by the orders list and the order detail modal.
 */

/** Rendered wherever an order has no value for a field. */
export const EMPTY_VALUE = '—';

/**
 * `Order.createdAt` and `Order.deliveryDate` are BOTH `AWSTimestamp` in the
 * GraphQL schema, but they are NOT the same unit. Do not "unify" these two
 * functions — reading either in the wrong unit renders a 1970 or a year-56000
 * date, and the two-digit year token hides it (millis-as-seconds prints "82").
 *
 * The clearest proof is one backend file that formats both fields for the
 * order PDF, `choco-backend/packages/orderService/src/domain/orderPdf/utils/
 * pdfUtils.tsx`:
 *
 *     formatCreatedAtDate:   DateTime.fromMillis(createdAt, { zone: 'utc' })
 *     formatDeliveryDate:    DateTime.fromSeconds(deliveryDate, { zone: 'utc' })
 *
 * Its tests pin both arithmetically: `createdAt: 1622724873783` renders
 * "Thu, June 3, 2021" and `deliveryDate: 1595321102` renders "Tue, July 21,
 * 2020". Corroborated by `orderCreateV2.controller.ts` stamping
 * `createdAt: Date.now()`, by `orderCreateV2/model.ts` typing the input as
 * `deliveryDate: UnixTimestamp.optional()`, and by
 * `publishOrderPendingBuyerApprovalCreatedEvent.ts` — "deliveryDate is a UNIX
 * timestamp (in seconds)" followed by `new Date(order.deliveryDate * 1000)`.
 * Legacy admin splits them the same way (`fromMillis` vs `fromSeconds`).
 *
 * `deliveryDate` is the ONLY seconds field on `Order`: `receivedAt` and
 * `confirmationTime` are milliseconds. Do not blanket-apply one unit.
 */

const CREATED_AT_FORMAT = 'dd MMM yy, HH:mm';
const DELIVERY_DATE_FORMAT = 'dd MMM yy';

const isUsableTimestamp = (value?: number | null): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/**
 * Format an order's creation time, including the time of day.
 *
 * @param createdAt - Epoch MILLISECONDS, as sent by the API
 */
export const formatOrderCreatedAt = (createdAt?: number | null): string => {
  if (!isUsableTimestamp(createdAt)) return EMPTY_VALUE;

  const created = DateTime.fromMillis(createdAt);
  return created.isValid ? created.toFormat(CREATED_AT_FORMAT) : EMPTY_VALUE;
};

/**
 * Format an order's requested delivery date.
 *
 * Read in UTC, matching legacy admin: a delivery date is a calendar day, so
 * resolving it in the viewer's own zone can shift it to the day before.
 *
 * @param deliveryDate - Epoch SECONDS, as sent by the API
 */
export const formatOrderDeliveryDate = (
  deliveryDate?: number | null
): string => {
  if (!isUsableTimestamp(deliveryDate)) return EMPTY_VALUE;

  const delivery = DateTime.fromSeconds(deliveryDate, { zone: 'utc' });
  return delivery.isValid
    ? delivery.toFormat(DELIVERY_DATE_FORMAT)
    : EMPTY_VALUE;
};

export type OrderStatusColor =
  | 'default'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger';

/**
 * Plain-language labels for the order statuses the schema actually defines
 * (`OrderStatus` in the AppSync schema). `created` is a deprecated alias of
 * `pending` — the schema says "use pending instead" — so it gets the same label.
 */
const ORDER_STATUS_LABELS: Readonly<Record<string, string>> = {
  created: 'Pending',
  pending: 'Pending',
  pendingBuyerApproval: 'Awaiting buyer approval',
  rejectedByBuyer: 'Rejected by buyer',
  confirmed: 'Confirmed',
  delivered: 'Delivered',
};

const ORDER_STATUS_COLORS: Readonly<Record<string, OrderStatusColor>> = {
  created: 'warning',
  pending: 'warning',
  pendingBuyerApproval: 'warning',
  rejectedByBuyer: 'danger',
  confirmed: 'success',
  delivered: 'success',
};

/**
 * Turn an unmapped status into something readable, so a status added to the
 * schema later shows as "Some new status" rather than `someNewStatus`.
 */
const humanizeStatus = (status: string): string => {
  const words = status
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .toLowerCase();

  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Label an order status for a CS reader. Never shows the raw schema value. */
export const getOrderStatusLabel = (status?: string | null): string => {
  if (!status) return EMPTY_VALUE;
  return ORDER_STATUS_LABELS[status] ?? humanizeStatus(status);
};

/** Chip colour for an order status. */
export const getOrderStatusColor = (
  status?: string | null
): OrderStatusColor => {
  if (!status) return 'default';
  return ORDER_STATUS_COLORS[status] ?? 'default';
};
